<script setup lang="ts">
/**
 * LanguageSwitch — 3 选项下拉菜单（简体中文 / 繁體中文 / English）
 * - 点击触发按钮展开 3 选项
 * - 当前语言 ✓ 标记
 * - 外部点击 / Esc 关闭；与账号下拉互斥（useHeaderMenu，2026-08-31）
 * - 展开/收起指示统一 lucide chevron-down + 180° 旋转（与账号下拉同形态）
 * - 键盘 a11y (aria-haspopup, aria-expanded, role="menu")
 * - 深色唯一（Nexus Cyberpunk）：玻璃态下拉面板
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocaleStore } from '@/stores/locale'
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/config'
import { useHeaderMenu } from '@/composables/useHeaderMenu'
import YfbIcon from '@/components/YfbIcon.vue'

const props = withDefaults(defineProps<{ menuId?: string }>(), {
  menuId: 'lang'
})

const { locale } = useI18n()
const store = useLocaleStore()
const menu = useHeaderMenu(props.menuId)
const open = menu.open

// 语言选项固定母语（不走 t()，用户看不懂界面语言时也能认出自己的母语）
const labels: Record<SupportedLocale, string> = {
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
  en: 'English'
}

const currentLabel = computed(() => labels[locale.value as SupportedLocale] || 'English')

const triggerRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLDivElement | null>(null)

function selectLocale(loc: SupportedLocale) {
  store.setLocale(loc)
  menu.close()
  triggerRef.value?.focus()
  // 同步 URL ?lang=(保留 redirect/hash 等其余 query),保证刷新/站内导航语言一致
  if (typeof window !== 'undefined') {
    const url = new URL(window.location.href)
    if (url.searchParams.get('lang') !== loc) {
      url.searchParams.set('lang', loc)
      window.history.replaceState(null, '', url.pathname + url.search + url.hash)
    }
  }
}

function onDocumentClick(e: MouseEvent) {
  if (!open.value) return
  const target = e.target as Node
  if (
    triggerRef.value?.contains(target) ||
    panelRef.value?.contains(target)
  ) {
    return
  }
  menu.close()
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value) return
  if (e.key === 'Escape') {
    e.preventDefault()
    menu.close()
    triggerRef.value?.focus()
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div class="lang-switch">
    <button
      ref="triggerRef"
      type="button"
      class="lang-switch__trigger site-header__tool"
      :aria-haspopup="'menu'"
      :aria-expanded="open"
      :aria-label="`Current language: ${currentLabel}, click to change`"
      :title="currentLabel"
      @click="menu.toggle"
    >
      <YfbIcon name="lucide:globe" :size="14" class="lang-switch__globe" aria-hidden="true" />
      <span class="lang-switch__current">{{ currentLabel }}</span>
      <YfbIcon
        name="lucide:chevron-down"
        :size="14"
        class="lang-switch__arrow shrink-0"
        :class="{ 'lang-switch__arrow--open': open }"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="open"
      ref="panelRef"
      class="lang-switch__panel"
      role="menu"
    >
      <button
        v-for="loc in SUPPORTED_LOCALES"
        :key="loc"
        type="button"
        role="menuitem"
        :lang="loc"
        class="lang-switch__option"
        :class="{ 'lang-switch__option--active': locale === loc }"
        @click="selectLocale(loc)"
      >
        <span class="lang-switch__check" aria-hidden="true">
          {{ locale === loc ? '✓' : '' }}
        </span>
        <span class="lang-switch__option-label">{{ labels[loc] }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.lang-switch {
  position: relative;
  display: inline-block;
}

.lang-switch__trigger {
  background-color: var(--yfb-surface);
  color: var(--yfb-ink);
  cursor: pointer;
  font-family: inherit;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.lang-switch__globe {
  flex-shrink: 0;
  color: var(--yfb-accent);
}

.lang-switch__trigger:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 12px var(--yfb-glow);
}

.lang-switch__current {
  letter-spacing: 0;
}

.lang-switch__arrow {
  color: var(--yfb-accent);
  transition: transform var(--yfb-anim-fast) ease-out;
}

.lang-switch__arrow--open {
  transform: rotate(180deg);
}

.lang-switch__panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 168px;
  background-color: var(--yfb-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--yfb-glass-blur));
  backdrop-filter: blur(var(--yfb-glass-blur));
  border: 1px solid var(--yfb-glass-border);
  border-radius: var(--yfb-radius-md);
  box-shadow: var(--yfb-shadow-2);
  z-index: 1000;
  overflow: hidden;
}

.lang-switch__option {
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-2);
  width: 100%;
  padding: var(--yfb-sp-3) var(--yfb-sp-4);
  background-color: transparent;
  color: var(--yfb-ink);
  border: 0;
  border-bottom: 1px solid var(--yfb-rule-soft);
  font-size: var(--yfb-fs-sm);
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: background-color var(--yfb-anim-fast) ease-out, color var(--yfb-anim-fast) ease-out;
}

.lang-switch__option:last-child {
  border-bottom: 0;
}

.lang-switch__option:hover {
  background-color: var(--yfb-surface-2);
  color: var(--yfb-accent);
}

.lang-switch__option--active {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

.lang-switch__option--active:hover {
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
}

.lang-switch__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  font-weight: 700;
  font-size: var(--yfb-fs-base);
}

.lang-switch__option-label {
  flex: 1;
}
</style>